import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import '../../styles/InvoiceGenerator.css';

const InvoiceGenerator = () => {
  const navigate = useNavigate();
  const invoiceRef = useRef();
  const [showPreview, setShowPreview] = useState(false);
  
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    date: '',
    customerName: '',
    items: [{ particulars: '', quantity: '', rate: '', amount: '' }]
  });

  const numberToWords = (num) => {
    if (!num || num === 0) return '';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const remainder = num % 1000;

    let result = '';
    if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
    if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
    if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
    if (remainder > 0) result += convertLessThanThousand(remainder);

    return result.trim();
  };

  const handleInputChange = (e) => {
    setInvoiceData({ ...invoiceData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: updatedItems });
  };

  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { particulars: '', quantity: '', rate: '', amount: '' }]
    });
  };

  const removeItem = (index) => {
    const updatedItems = invoiceData.items.filter((_, i) => i !== index);
    setInvoiceData({ ...invoiceData, items: updatedItems });
  };

  const calculateTotal = () => {
    return invoiceData.items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const handlePrintPDF = () => {
    const element = invoiceRef.current;
    const filename = `Invoice_${invoiceData.invoiceNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const options = {
      margin: [5, 5, 5, 5],
      filename: filename,
      image: { type: 'png', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(options).from(element).save();
  };

  return (
    <div className="invoice-container">
      <div className="invoice-header-top">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1>Invoice Generator</h1>
      </div>

      {!showPreview ? (
        <div className="invoice-form">
          <div className="form-section">
            <h3>Invoice Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Invoice Number *</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={invoiceData.invoiceNumber}
                  onChange={handleInputChange}
                  placeholder="201"
                  required
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={invoiceData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Customer Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Customer Name (M/s.) *</label>
                <input
                  type="text"
                  name="customerName"
                  value={invoiceData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter customer name"
                  required
                />
              </div>
            </div>
          </div>

          <div className="items-section">
            <h3>Items/Particulars</h3>
            {invoiceData.items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="form-group">
                  <label>Particulars</label>
                  <input
                    type="text"
                    value={item.particulars}
                    onChange={(e) => handleItemChange(index, 'particulars', e.target.value)}
                    placeholder="Item description"
                  />
                </div>
                <div className="form-group">
                  <label>QTY (Optional)</label>
                  <input
                    type="text"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    placeholder=""
                  />
                </div>
                <div className="form-group">
                  <label>Rate (Optional)</label>
                  <input
                    type="text"
                    value={item.rate}
                    onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                    placeholder=""
                  />
                </div>
                <div className="form-group">
                  <label>Amount (Rs.)</label>
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
                {invoiceData.items.length > 1 && (
                  <button type="button" onClick={() => removeItem(index)} className="remove-btn">
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addItem} className="add-item-btn">
              + Add Item
            </button>
          </div>

          <div className="totals-preview">
            <div className="total-row grand-total">
              <span>TOTAL:</span>
              <span>₹ {calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={() => setShowPreview(true)} className="generate-btn">
              Generate Invoice Preview
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="action-buttons no-print">
            <button onClick={() => setShowPreview(false)} className="edit-btn" style={{ marginRight: '2rem' }}>
              ← Edit Invoice
            </button>
            <button onClick={handlePrintPDF} className="download-pdf-btn">
              🖨️ Download PDF
            </button>
          </div>

          <div className="invoice-preview">
            <div className="invoice-document" ref={invoiceRef}>
              {/* Header Section */}
              <div className="invoice-header-doc">
                <div className="header-top">
                  <div className="logo-section">
                    <div className="logo-placeholder">
  <svg width="70" height="70" viewBox="0 0 120 100" xmlns="http://www.w3.org/2000/svg">
    {/* Main horizontal jib (arm) */}
    <rect x="5" y="12" width="110" height="4" fill="#000"/>
    
    {/* Top zigzag pattern on jib */}
    <path d="M 10,12 L 15,8 L 20,12 L 25,8 L 30,12 L 35,8 L 40,12 L 45,8 L 50,12 L 55,8 L 60,12 L 65,8 L 70,12 L 75,8 L 80,12 L 85,8 L 90,12 L 95,8 L 100,12 L 105,8 L 110,12" 
          stroke="#000" strokeWidth="1.5" fill="none"/>
    
    {/* Support triangles */}
    <polygon points="10,16 15,22 5,22" fill="#000"/>
    <polygon points="110,16 115,22 105,22" fill="#000"/>
    
    {/* Vertical tower structure */}
    <rect x="56" y="16" width="8" height="60" fill="none" stroke="#000" strokeWidth="2"/>
    
    {/* Tower cross beams */}
    <rect x="50" y="22" width="20" height="2" fill="#000"/>
    <rect x="50" y="30" width="20" height="2" fill="#000"/>
    <rect x="50" y="38" width="20" height="2" fill="#000"/>
    <rect x="50" y="46" width="20" height="2" fill="#000"/>
    <rect x="50" y="54" width="20" height="2" fill="#000"/>
    <rect x="50" y="62" width="20" height="2" fill="#000"/>
    <rect x="50" y="70" width="20" height="2" fill="#000"/>
    
    {/* Diagonal lattice supports */}
    <line x1="50" y1="22" x2="56" y2="30" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="22" x2="64" y2="30" stroke="#000" strokeWidth="1.5"/>
    <line x1="50" y1="30" x2="56" y2="38" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="30" x2="64" y2="38" stroke="#000" strokeWidth="1.5"/>
    <line x1="50" y1="38" x2="56" y2="46" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="38" x2="64" y2="46" stroke="#000" strokeWidth="1.5"/>
    <line x1="50" y1="46" x2="56" y2="54" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="46" x2="64" y2="54" stroke="#000" strokeWidth="1.5"/>
    <line x1="50" y1="54" x2="56" y2="62" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="54" x2="64" y2="62" stroke="#000" strokeWidth="1.5"/>
    <line x1="50" y1="62" x2="56" y2="70" stroke="#000" strokeWidth="1.5"/>
    <line x1="70" y1="62" x2="64" y2="70" stroke="#000" strokeWidth="1.5"/>
    
    {/* Crane hook and cable */}
    <line x1="95" y1="16" x2="95" y2="40" stroke="#000" strokeWidth="2"/>
    <rect x="91" y="40" width="8" height="6" fill="#000"/>
    
    {/* Base platform */}
    <rect x="40" y="76" width="40" height="4" fill="#000"/>
    <rect x="45" y="80" width="30" height="3" fill="#000"/>
    
    {/* Blue accent */}
    <rect x="10" y="8" width="100" height="2" fill="#0066cc"/>
  </svg>
</div>


                    <div className="company-name">
                      <h1>Bricks & Brain</h1>
                      <p className="tagline">Civil Contractor and Interior Decorator</p>
                    </div>
                  </div>
                  <div className="header-right">
                    <p className="jurisdiction">Subject to Palghar Jurisdiction</p>
                    <p className="mobile">Mobile : 9049610819</p>
                  </div>
                </div>
                <div className="address-line">
                  D-001, Periyar Building, Babhola Naka, Opp. D'mart, Vasai (West), Dist. Palghar - 401 202.
                </div>
              </div>

              {/* Invoice Number and Date */}
              <div className="invoice-meta">
                <div className="invoice-no">
                  <span>No.: </span><strong>{invoiceData.invoiceNumber}</strong>
                </div>
                <div className="invoice-date">
                  <span>Date : </span><strong>{invoiceData.date ? new Date(invoiceData.date).toLocaleDateString('en-GB').replace(/\//g, '-') : '__________'}</strong>
                </div>
              </div>

              {/* Customer Name */}
              <div className="customer-line">
                <span>M/s. </span>
                <span className="customer-name-underline">{invoiceData.customerName}</span>
              </div>

              {/* Invoice Table */}
              <table className="invoice-table-original">
                <thead>
                  <tr>
                    <th style={{width: '8%'}}>Sr. No.</th>
                    <th style={{width: '52%'}}>PARTICULARS</th>
                    <th style={{width: '10%'}}>QTY.</th>
                    <th style={{width: '15%'}}>Rate</th>
                    <th style={{width: '15%'}}>Amount<br/>Rs. &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; P.</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.items.map((item, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item.particulars}</td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-right">{item.rate}</td>
                      <td className="text-right">{item.amount ? parseFloat(item.amount).toFixed(2) : ''}</td>
                    </tr>
                  ))}
                  {/* Add empty rows */}
                  {[...Array(Math.max(0, 12 - invoiceData.items.length))].map((_, i) => (
                    <tr key={`empty-${i}`}>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr className="total-row-table">
                    <td colSpan="3" className="text-right"><strong>E.&O.E.</strong></td>
                    <td className="text-center"><strong>TOTAL</strong></td>
                    <td className="text-right"><strong>{calculateTotal().toFixed(2)}</strong></td>
                  </tr>
                </tbody>
              </table>

              {/* Footer Section */}
              <div className="invoice-footer-original">
                <div className="rupees-line">
                  <span className="rupees-label">Rupees</span>
                  <span className="rupees-words">{numberToWords(Math.round(calculateTotal()))}</span>
                </div>
                <div className="signature-section-right">
                  <p className="for-company">For Bricks & Brain</p>
                  <p className="proprietor-label">Proprietor</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceGenerator;
